<section class="d-flex flex-column gap-2" data-testid="device-estate-page">
  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.devices.estate.title' | translate }}</h2>
      <p class="av-meta m-0">{{ 'backoffice.devices.estate.subtitle' | translate }}</p>
    </div>

    <button type="button" class="av-btn" (click)="reload()" data-testid="estate-reload">
      <mat-icon aria-hidden="true">refresh</mat-icon>
      <span>{{ 'backoffice.common.reload' | translate }}</span>
    </button>
  </div>

  <p class="av-meta m-0" data-testid="estate-scope-note">
    {{ 'backoffice.devices.estate.scope_note' | translate }}
  </p>

  <div class="d-flex flex-wrap align-items-end gap-2">
    <av-organization-picker
      [organizations]="facade.organizations()"
      [value]="facade.organizationId()"
      (valueChange)="selectOrganization($event)"
    ></av-organization-picker>
  </div>
</section>

@if (!facade.hasOrganization()) {
  <av-empty-state
    class="mt-3"
    icon="corporate_fare"
    [title]="'backoffice.devices.estate.pick_title' | translate"
    [message]="'backoffice.devices.estate.pick_message' | translate"
  ></av-empty-state>
} @else {
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        class="mt-3"
        icon="error_outline"
        [title]="'backoffice.devices.error.title' | translate"
        [message]="'backoffice.devices.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="estate-counts">
        <h3 class="av-section-title m-0">
          {{ 'backoffice.devices.estate.counts_title' | translate }}
        </h3>

        <div class="d-flex flex-wrap gap-4">
          @for (option of stateOptions; track option) {
            <div class="d-flex flex-column gap-1">
              <span class="av-meta">{{ stateLabelKeys[option] | translate }}</span>
              <span class="cell-strong" [attr.data-testid]="'count-' + option">
                {{ countFor(option) }}
              </span>
            </div>
          }
        </div>

        <p class="av-meta m-0" data-testid="estate-inactive-rule">
          {{ 'backoffice.devices.estate.inactive_rule' | translate }}
        </p>
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="estate-reaping">
        <div class="d-flex flex-column gap-1">
          <h3 class="av-section-title m-0">
            {{ 'backoffice.devices.reaping.title' | translate }}
          </h3>
          <p class="av-meta m-0">{{ 'backoffice.devices.reaping.hint' | translate }}</p>
        </div>

        @if (facade.reapableCount() === 0) {
          <p class="av-meta m-0" data-testid="reaping-none">
            {{ 'backoffice.devices.reaping.none' | translate }}
          </p>
        } @else {
          <div class="av-banner p-3 d-flex align-items-start gap-2" data-testid="reaping-pending">
            <mat-icon aria-hidden="true">auto_delete</mat-icon>
            <div class="d-flex flex-column gap-2">
              <span>
                {{
                  'backoffice.devices.reaping.summary'
                    | translate
                      : { reapable: facade.reapableCount(), inactive: facade.inactiveCount() }
                }}
                {{ 'backoffice.devices.reaping.pending' | translate }}
              </span>

              <button
                type="button"
                class="av-btn align-self-start"
                (click)="showReapable()"
                data-testid="reaping-filter-button"
              >
                {{ 'backoffice.devices.reaping.show' | translate }}
              </button>
            </div>
          </div>
        }
      </section>

      <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="estate-list">
        <div class="filter-row d-flex flex-wrap align-items-end gap-2">
          <label class="d-flex flex-column gap-1" for="device-search">
            <span class="av-meta">{{ 'backoffice.devices.filters.search' | translate }}</span>
            <input
              id="device-search"
              type="search"
              class="av-field"
              [value]="facade.filters().search"
              (change)="changeFilter('search', $any($event.target).value)"
              data-testid="device-search"
            />
          </label>

          <label class="d-flex flex-column gap-1" for="device-state">
            <span class="av-meta">{{ 'backoffice.devices.filters.state' | translate }}</span>
            <select
              id="device-state"
              class="av-field"
              [value]="facade.filters().state"
              (change)="changeFilter('state', $any($event.target).value)"
              data-testid="device-state-filter"
            >
              <option value="">{{ 'backoffice.devices.filters.any_state' | translate }}</option>
              @for (option of stateOptions; track option) {
                <option [value]="option">{{ stateLabelKeys[option] | translate }}</option>
              }
            </select>
          </label>

          @if (facade.hasActiveFilters()) {
            <button
              type="button"
              class="av-btn"
              (click)="clearFilters()"
              data-testid="device-clear-filters"
            >
              {{ 'backoffice.devices.filters.clear' | translate }}
            </button>
          }
        </div>

        <p class="av-meta m-0" data-testid="device-search-hint">
          {{ 'backoffice.devices.filters.search_hint' | translate }}
        </p>

        @if (isEmpty()) {
          <av-empty-state
            icon="devices"
            [title]="'backoffice.devices.empty.filtered_title' | translate"
            [message]="'backoffice.devices.empty.filtered_message' | translate"
          ></av-empty-state>
        } @else {
          <p class="av-meta m-0" data-testid="no-agent-version-note">
            {{ 'backoffice.devices.detail.no_agent_version' | translate }}
          </p>

          <div class="table-scroll">
            <table class="av-table w-100" data-testid="device-table">
              <thead>
                <tr>
                  <th scope="col">{{ 'backoffice.devices.table.device' | translate }}</th>
                  <th scope="col">{{ 'backoffice.devices.table.state' | translate }}</th>
                  <th scope="col">{{ 'backoffice.devices.table.last_seen' | translate }}</th>
                  <th scope="col">
                    {{ 'backoffice.devices.table.inactivity_reference' | translate }}
                  </th>
                  <th scope="col">{{ 'backoffice.devices.table.licence' | translate }}</th>
                  <th scope="col"></th>
                </tr>
              </thead>

              <tbody>
                @for (device of facade.devices(); track device.id) {
                  <tr>
                    <td>
                      <button
                        type="button"
                        class="link-button cell-strong"
                        (click)="selectDevice(device.id)"
                        data-testid="device-select"
                      >
                        {{ device.displayName }}
                      </button>

                      <div class="av-source">{{ device.azureADDeviceId || unresolved }}</div>
                    </td>

                    <td>
                      <av-status-chip
                        [label]="device.stateLabelKey | translate"
                        [tone]="device.stateTone"
                      ></av-status-chip>
                    </td>

                    <td class="cell-muted" data-testid="device-last-seen">
                      {{
                        device.hasNeverCheckedIn
                          ? ('backoffice.devices.table.never_seen' | translate)
                          : (device.last_seen_at | date: dateTimeFormat)
                      }}
                    </td>

                    <td class="cell-muted">
                      {{
                        device.inactivity_reference
                          ? (device.inactivity_reference | date: dateTimeFormat)
                          : unresolved
                      }}
                    </td>

                    <td class="cell-muted">{{ device.licenceStateLabelKey | translate }}</td>

                    <td>
                      <button
                        type="button"
                        class="av-btn av-btn-danger"
                        [disabled]="!facade.canRetire() || device.isRetired"
                        [matTooltip]="
                          device.isRetired ? ('backoffice.devices.retire.already' | translate) : ''
                        "
                        (click)="openRetire(device)"
                        data-testid="device-retire"
                      >
                        {{ 'backoffice.devices.retire.button' | translate }}
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <av-paginator
            [page]="facade.page()"
            [pageSize]="facade.pageSize()"
            [total]="facade.total()"
            (pageChange)="goToPage($event)"
            (pageSizeChange)="changePageSize($event)"
          ></av-paginator>
        }
      </section>

      @if (facade.selectedDeviceId()) {
        <section class="av-card mt-3 p-4 d-flex flex-column gap-3" data-testid="device-detail">
          <h3 class="av-section-title m-0">
            {{ 'backoffice.devices.detail.title' | translate }}
          </h3>

          @switch (facade.selectedDeviceState()) {
            @case ('error') {
              <p class="av-meta m-0" data-testid="device-detail-error">
                {{ 'backoffice.devices.detail.error' | translate }}
              </p>
            }
            @case ('ready') {
              @if (facade.selectedDevice(); as device) {
                <div class="d-flex flex-wrap gap-4">
                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.name' | translate
                    }}</span>
                    <span class="cell-strong">{{ device.displayName }}</span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.state' | translate
                    }}</span>
                    <av-status-chip
                      [label]="device.stateLabelKey | translate"
                      [tone]="device.stateTone"
                    ></av-status-chip>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.reports' | translate
                    }}</span>
                    <span class="cell-strong">{{ device.report_count }}</span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.last_report' | translate
                    }}</span>
                    <span class="cell-strong">
                      {{
                        device.last_report_at
                          ? (device.last_report_at | date: dateTimeFormat)
                          : unresolved
                      }}
                    </span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.os_version' | translate
                    }}</span>
                    <span class="cell-strong" data-testid="device-os-version">
                      {{ device.osCaption }} · {{ device.osVersion }}
                    </span>
                  </div>

                  <div class="d-flex flex-column gap-1">
                    <span class="av-meta">{{
                      'backoffice.devices.detail.bios_version' | translate
                    }}</span>
                    <span class="cell-strong">{{ device.biosVersion }}</span>
                  </div>
                </div>

                <div class="d-flex flex-column gap-1">
                  <h4 class="av-section-title m-0">
                    {{ 'backoffice.devices.detail.collected_title' | translate }}
                  </h4>
                  <p class="av-meta m-0">
                    {{ 'backoffice.devices.detail.collected_note' | translate }}
                  </p>
                </div>

                @if (device.hasCollectedData) {
                  <pre class="av-source collected-json" data-testid="device-collected-json">{{
                    device.collectedJson
                  }}</pre>
                } @else {
                  <p class="av-meta m-0" data-testid="device-collected-empty">
                    {{ 'backoffice.devices.detail.collected_empty' | translate }}
                  </p>
                }
              }
            }
            @default {
              <p class="av-meta m-0">{{ 'backoffice.devices.detail.loading' | translate }}</p>
            }
          }
        </section>
      }
    }
    @default {
      <av-empty-state
        class="mt-3"
        icon="hourglass_empty"
        [title]="'backoffice.devices.estate.loading_title' | translate"
        [message]="'backoffice.devices.estate.loading_message' | translate"
      ></av-empty-state>
    }
  }
}
